/**
 * MediaAttachment.js
 *
 * Renders a customer's inbound media (photo/video/audio/document) inside an agent
 * conversation. We never store the bytes — the chip shows an icon + filename; the
 * actual file is streamed from the backend proxy (authenticated) only when the agent
 * clicks Preview or Download.
 *
 * Preview and Download share a SINGLE fetch: the first click fetches the bytes into a
 * blob and memoizes an object URL, so previewing and then downloading (or toggling the
 * preview off and on) never re-hits the network. The backend additionally caches the
 * bytes briefly, so a *second agent* opening the same conversation is served locally
 * too.
 *
 * Props:
 *   - media: {
 *       kind: 'image'|'video'|'audio'|'document'|'sticker',
 *       mime, filename, caption?, size?, placeholder?,
 *       download_token          // signed token minted by the history/SSE endpoint
 *     }
 *   - workflowId: workflow id (for the proxy endpoint)
 *   - align: 'start' | 'end'    // bubble side, for subtle color only (optional)
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Image, Video, Music, FileText, File,
  Download, Loader2, AlertCircle, Eye, EyeOff,
} from 'lucide-react';
import { apiCall } from './Api';

const ICONS = {
  image: Image,
  video: Video,
  audio: Music,
  document: FileText,
  sticker: Image,
};

const KIND_LABEL = {
  image: 'Photo',
  video: 'Video',
  audio: 'Audio',
  document: 'Document',
  sticker: 'Sticker',
};

// Media kinds we can render inline. Documents can't be previewed generically.
const PREVIEWABLE = new Set(['image', 'video', 'audio', 'sticker']);

// Above this size we don't auto-inline video/audio (avoid buffering a huge blob in
// the tab); the agent can still Download. Images always preview.
const INLINE_MAX_BYTES = 30 * 1024 * 1024;

const humanSize = (bytes) => {
  if (!bytes || bytes <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i += 1; }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};

const MediaAttachment = ({ media, workflowId, align = 'start' }) => {
  const [state, setState] = useState('idle');   // idle | loading | error
  const [showPreview, setShowPreview] = useState(false);
  const objectUrlRef = useRef(null);            // memoized blob URL (one fetch, reused)
  const [objectUrl, setObjectUrl] = useState(null);

  // Revoke the blob URL when this chip unmounts so we don't leak memory.
  useEffect(() => () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  const media_ = (media && typeof media === 'object') ? media : null;
  const kind = (media_ && media_.kind) || 'document';
  const Icon = ICONS[kind] || File;
  const filename = (media_ && media_.filename) || 'file';
  const label = KIND_LABEL[kind] || 'File';
  const token = media_ && media_.download_token;
  const size = humanSize(media_ && media_.size);
  const canPreview = !!media_ && PREVIEWABLE.has(kind)
    && !(kind !== 'image' && media_.size && media_.size > INLINE_MAX_BYTES);

  // Fetch once (through the authenticated proxy) and memoize the object URL.
  const ensureBlobUrl = useCallback(async () => {
    if (objectUrlRef.current) return objectUrlRef.current;
    if (!token) throw new Error('no token');
    const resp = await apiCall(
      `/api/chatbot/workflows/${workflowId}/media?m=${encodeURIComponent(token)}`
    );
    if (!resp || !resp.ok) throw new Error(`HTTP ${resp && resp.status}`);
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    objectUrlRef.current = url;
    setObjectUrl(url);
    return url;
  }, [token, workflowId]);

  const handlePreview = useCallback(async () => {
    if (showPreview) { setShowPreview(false); return; }   // toggle off (blob kept)
    setState('loading');
    try {
      await ensureBlobUrl();
      setShowPreview(true);
      setState('idle');
    } catch (err) {
      console.error('Media preview failed:', err);
      setState('error');
    }
  }, [showPreview, ensureBlobUrl]);

  const handleDownload = useCallback(async () => {
    setState('loading');
    try {
      const url = await ensureBlobUrl();       // reuses the preview fetch if present
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setState('idle');
    } catch (err) {
      console.error('Media download failed:', err);
      setState('error');
    }
  }, [ensureBlobUrl, filename]);

  if (!media_) return null;

  const tone = align === 'end'
    ? 'bg-white/15 border-white/25'
    : 'bg-gray-50 border-gray-200';

  const StatusIcon = state === 'loading' ? Loader2 : state === 'error' ? AlertCircle : null;

  return (
    <div className="mt-1">
      <div
        className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 ${tone}`}
        style={{ maxWidth: 300 }}
      >
        <span className="shrink-0 grid place-items-center w-8 h-8 rounded-md bg-black/5">
          <Icon className="w-4 h-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium truncate" title={filename}>{filename}</span>
          <span className="block text-[11px] opacity-70">
            {label}{size ? ` \u00b7 ${size}` : ''}
          </span>
        </span>

        {canPreview && (
          <button
            type="button"
            onClick={handlePreview}
            disabled={state === 'loading' || !token}
            title={showPreview ? 'Hide preview' : 'Preview'}
            className={`shrink-0 p-1.5 rounded-md hover:bg-black/10 transition-colors ${(!token) ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}

        <button
          type="button"
          onClick={handleDownload}
          disabled={state === 'loading' || !token}
          title={token ? `Download ${filename}` : 'Media unavailable (reload the conversation)'}
          className={`shrink-0 p-1.5 rounded-md hover:bg-black/10 transition-colors ${(!token) ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {StatusIcon
            ? <StatusIcon className={`w-4 h-4 ${state === 'loading' ? 'animate-spin' : 'text-red-500'}`} />
            : <Download className="w-4 h-4 opacity-80" />}
        </button>
      </div>

      {state === 'error' && (
        <div className="text-[11px] text-red-500 mt-1">
          Couldn&rsquo;t fetch this file. Tap to retry.
        </div>
      )}

      {showPreview && objectUrl && (
        <div className="mt-2">
          {(kind === 'image' || kind === 'sticker') && (
            <a href={objectUrl} target="_blank" rel="noopener noreferrer" title="Open full size">
              <img
                src={objectUrl}
                alt={filename}
                className="rounded-lg border border-black/10"
                style={{ maxWidth: 300, maxHeight: 320, objectFit: 'contain', display: 'block' }}
              />
            </a>
          )}
          {kind === 'video' && (
            <video
              src={objectUrl}
              controls
              className="rounded-lg border border-black/10"
              style={{ maxWidth: 320, maxHeight: 320, display: 'block' }}
            />
          )}
          {kind === 'audio' && (
            <audio src={objectUrl} controls style={{ maxWidth: 300, display: 'block' }} />
          )}
        </div>
      )}
    </div>
  );
};

export default MediaAttachment;
